import { useCallback, useEffect, useState } from 'react';
import { getStepClassification, patchStepClassification } from '../services';
import { IStepClassificationItem, StepClassification, StepClassificationWritable } from '../types';
import { resolveErrorMessage } from './hookUtils';

export interface UseAdminStepClassificationResult {
	data: IStepClassificationItem[] | null;
	loading: boolean;
	error: string | null;
	patching: string | null;
	refetch: () => void;
	classify: (stepId: string, classification: StepClassificationWritable) => Promise<void>;
}

export function useAdminStepClassification(
	statusFilter?: StepClassification,
	agencyId?: number,
	includeExcluded?: boolean,
): UseAdminStepClassificationResult {
	const [data, setData] = useState<IStepClassificationItem[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [patching, setPatching] = useState<string | null>(null);
	const [fetchCount, setFetchCount] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);

		getStepClassification({
			...(statusFilter ? { status: statusFilter } : {}),
			...(agencyId !== undefined ? { agencyId } : {}),
			...(includeExcluded ? { includeExcluded: true } : {}),
		})
			.then((items) => {
				if (!cancelled) {
					setData(items);
					setLoading(false);
				}
			})
			.catch((err) => {
				if (!cancelled) {
					setError(resolveErrorMessage(err));
					setLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [statusFilter, agencyId, includeExcluded, fetchCount]);

	const refetch = useCallback(() => setFetchCount((n) => n + 1), []);

	// Actualización optimista: aplica el cambio en local, rollback si falla
	const classify = useCallback(
		async (stepId: string, classification: StepClassificationWritable) => {
			if (!data) return;

			const previousData = data;
			setPatching(stepId);
			setData((prev) =>
				prev ? prev.map((item) => (item.stepId === stepId ? { ...item, classification } : item)) : prev,
			);

			try {
				const updated = await patchStepClassification(stepId, classification);
				setData((prev) => (prev ? prev.map((item) => (item.stepId === stepId ? updated : item)) : prev));
			} catch (err) {
				setData(previousData);
				setError(resolveErrorMessage(err));
			} finally {
				setPatching(null);
			}
		},
		[data],
	);

	return { data, loading, error, patching, refetch, classify };
}
